import { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Diagnostic page for the milk rollup bug.
 * Hits /api/debug/milk-rollup with the caller's own auth token and
 * renders the JSON response so the user can copy/paste it without
 * needing F12 / a developer console on mobile.
 */
export default function DebugMilkRollup() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await axios.get(`${API_URL}/api/debug/milk-rollup`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const copyJson = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed — long-press the text below to select all');
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6" data-testid="debug-milk-rollup-page">
      <div className="max-w-2xl mx-auto space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Milk Rollup Diagnostic</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Tap the button below to fetch your raw milk-production data from
              the server. Copy the JSON and paste it back to support so we can
              diagnose why your Market Dairy rollup row is empty.
            </p>
            <Button
              onClick={run}
              disabled={loading}
              data-testid="debug-milk-run-btn"
              className="w-full"
            >
              {loading ? 'Loading…' : 'Run diagnostic'}
            </Button>
            {error && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3"
                data-testid="debug-milk-error"
              >
                Error: {error}
              </div>
            )}
            {data && (
              <>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Stat label="Total records (all time)" value={data.total_records_all_time} />
                  <Stat label="In last 30 days" value={data.records_in_last_30_days} />
                  <Stat label="In last 365 days" value={data.records_in_last_365_days} />
                  <Stat label="Per-animal (365d)" value={data.per_animal_records_in_365} />
                  <Stat label="Herd-level (365d)" value={data.herd_level_records_in_365} />
                  <Stat label="Today (UTC)" value={data.today_utc} />
                </div>
                <Button
                  onClick={copyJson}
                  variant="outline"
                  className="w-full"
                  data-testid="debug-milk-copy-btn"
                >
                  Copy full JSON
                </Button>
                <pre
                  className="text-xs bg-muted p-3 rounded overflow-auto max-h-96 whitespace-pre-wrap break-words"
                  data-testid="debug-milk-json"
                >
                  {JSON.stringify(data, null, 2)}
                </pre>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-muted/40 rounded p-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-base font-semibold">{String(value ?? '—')}</div>
    </div>
  );
}
